<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { z } from 'zod'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import { ipPoolStatusClass } from '@/utils/vserverStatus'

const IpPoolEntry = z.object({
  address: z.string(),
  version: z.enum(['v4', 'v6']),
  status: z.enum(['available', 'allocated', 'reserved']),
  ptr_record: z.string().nullable().optional(),
  subnet: z.string().nullable().optional(),
  gateway: z.string().nullable().optional(),
  allocated_at: z.string().nullable().optional(),
  vserver_hostname: z.string().nullable().optional()
})
type IpPoolEntry = z.infer<typeof IpPoolEntry>

const IpPoolResponse = z.object({
  entries: z.array(IpPoolEntry),
  available_count: z.number(),
  allocated_count: z.number(),
  reserved_count: z.number()
})

const { t } = useI18n()
const notifyStore = useNotifyStore()

const loading = ref(true)
const entries = ref<IpPoolEntry[]>([])
const counts = ref({ available: 0, allocated: 0, reserved: 0 })

const newAddress = ref('')
const newSubnet = ref('')
const newGateway = ref('')
const newVersion = ref<'v4' | 'v6'>('v4')
const submitting = ref(false)

const isLow = computed(() => counts.value.available < 5)

async function fetchPool() {
  loading.value = true
  try {
    const res = await useAxios<null, z.infer<typeof IpPoolResponse>>(
      'GET',
      '/admin/vserver/ip-pool',
      true
    )
    if (res?.status === STATUS_CODES.OK) {
      const parsed = IpPoolResponse.safeParse(res.data)
      if (parsed.success) {
        entries.value = parsed.data.entries
        counts.value = {
          available: parsed.data.available_count,
          allocated: parsed.data.allocated_count,
          reserved: parsed.data.reserved_count
        }
      }
    }
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AdminVServerIpPool' })
  } finally {
    loading.value = false
  }
}

async function addIp() {
  if (!newAddress.value.trim()) return
  submitting.value = true
  try {
    await useAxios('POST', '/admin/vserver/ip-pool', true, {
      address: newAddress.value.trim(),
      version: newVersion.value,
      subnet: newSubnet.value.trim() || undefined,
      gateway: newGateway.value.trim() || undefined
    })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.vserver.admin.ipPool.title'),
      message: t('dashboard.vserver.admin.ipPool.addSuccess')
    })
    newAddress.value = ''
    newSubnet.value = ''
    newGateway.value = ''
    await fetchPool()
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AdminVServerIpPool:add' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.vserver.errors.actionFailed'),
      message: getErrorMessage(err)
    })
  } finally {
    submitting.value = false
  }
}

const statusClass = ipPoolStatusClass

onMounted(fetchPool)
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.vserver.admin.ipPool.title')"
    :subtitle="t('dashboard.vserver.admin.ipPool.subtitle')"
    icon="mdi-ip-network"
    icon-color="cyan"
    show-back
    :back-route="{ name: 'dashboardAdminVServer' }"
  >
    <div class="stats-row">
      <div class="stat-card" :class="{ low: isLow }">
        <span class="stat-label">{{ $t('dashboard.vserver.admin.ipPool.available') }}</span>
        <span class="stat-value">{{ counts.available }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">{{ $t('dashboard.vserver.admin.ipPool.allocated') }}</span>
        <span class="stat-value">{{ counts.allocated }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">{{ $t('dashboard.vserver.admin.ipPool.reserved') }}</span>
        <span class="stat-value">{{ counts.reserved }}</span>
      </div>
    </div>

    <v-alert v-if="isLow" type="warning" variant="tonal" class="warn-alert">
      {{ $t('dashboard.vserver.admin.ipPool.lowWarning', { count: counts.available }) }}
    </v-alert>

    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title">{{ $t('dashboard.vserver.admin.ipPool.addTitle') }}</h3>
      </div>
      <div class="add-form">
        <v-text-field
          v-model="newAddress"
          :label="$t('dashboard.vserver.admin.ipPool.addressLabel')"
          variant="outlined"
          density="compact"
          hide-details
          class="form-input"
        />
        <v-select
          v-model="newVersion"
          :items="[{ value: 'v4', title: 'IPv4' }, { value: 'v6', title: 'IPv6' }]"
          :label="$t('dashboard.vserver.admin.ipPool.versionLabel')"
          variant="outlined"
          density="compact"
          hide-details
          class="form-input narrow"
        />
        <v-text-field
          v-model="newSubnet"
          :label="$t('dashboard.vserver.admin.ipPool.subnetLabel')"
          variant="outlined"
          density="compact"
          hide-details
          class="form-input"
        />
        <v-text-field
          v-model="newGateway"
          :label="$t('dashboard.vserver.admin.ipPool.gatewayLabel')"
          variant="outlined"
          density="compact"
          hide-details
          class="form-input"
        />
        <button
          type="button"
          class="btn-create"
          :disabled="submitting || !newAddress.trim()"
          @click="addIp"
        >
          <v-icon icon="mdi-plus" size="18" />
          {{ $t('dashboard.vserver.admin.ipPool.addAction') }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <div v-else class="section-card">
      <div class="data-table">
        <div class="table-header pool-grid">
          <span>{{ $t('dashboard.vserver.admin.ipPool.table.address') }}</span>
          <span>{{ $t('dashboard.vserver.admin.ipPool.table.version') }}</span>
          <span>{{ $t('dashboard.vserver.admin.ipPool.table.status') }}</span>
          <span>{{ $t('dashboard.vserver.admin.ipPool.table.allocatedTo') }}</span>
          <span>{{ $t('dashboard.vserver.admin.ipPool.table.allocatedAt') }}</span>
        </div>
        <div v-for="entry in entries" :key="entry.address" class="table-row pool-grid">
          <span class="mono">{{ entry.address }}</span>
          <span>{{ entry.version }}</span>
          <span>
            <span class="status-chip" :class="statusClass(entry.status)">{{ entry.status }}</span>
          </span>
          <span class="mono">{{ entry.vserver_hostname ?? '-' }}</span>
          <span class="text-dimmed">{{ entry.allocated_at ?? '-' }}</span>
        </div>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 16px;
}

.stat-card {
  background: rgba(120, 163, 255, 0.05);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.low {
    border-color: rgba(255, 183, 77, 0.4);
    background: rgba(255, 183, 77, 0.08);
  }
}

.stat-label {
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.warn-alert {
  margin-bottom: 16px;
}

.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
}

.section-header {
  margin-bottom: 12px;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.add-form {
  display: grid;
  grid-template-columns: 1fr 0.6fr 1fr 1fr auto;
  gap: 10px;
  align-items: center;
}

@media (max-width: 900px) {
  .add-form {
    grid-template-columns: 1fr 1fr;
  }
}

.form-input.narrow {
  max-width: 140px;
}

.btn-create {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #00B0FF, #1FE2D4);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.data-table {
  overflow-x: auto;
}

.pool-grid {
  grid-template-columns: 1.3fr 0.5fr 0.8fr 1fr 1fr;
  min-width: 720px;
}

.table-header, .table-row {
  display: grid;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
}

.mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
}

.text-dimmed {
  color: rgba(200, 218, 255, 0.5);
}

.state-box {
  display: flex;
  justify-content: center;
  padding: 48px;
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
}

.status-success {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-info {
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.3);
  color: #4fc3f7;
}

.status-warning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.status-grey {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.5);
}
</style>
